import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useAppSelector } from "Redux/hooks";

const DraggableHeaderRow = ({
  id,
  header,
  priceShow,
  onChange,
  onDelete,
}) => {
  const rightToLeft = useAppSelector(
    (state) => state.root.rightToLeft
  );

  const [menuOpen, setMenuOpen] = useState(false);

  const menuRef = useRef(null);
  const titleInputRef = useRef(null);
  const [menuPlacement, setMenuPlacement] = useState("down");
  const menuButtonRef = useRef(null);


  const isEditing = Boolean(
    header.isEditing ?? header.isNew
  );

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const totalColumns = priceShow ? 10 : 8;


  const handleMenuButtonRef = (node) => {
  menuButtonRef.current = node;
  setActivatorNodeRef(node);
};


  const rowStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
    zIndex: isDragging || menuOpen ? 100 : 1,
  };

  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }

    const handleOutsideClick = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
      }
    };

    document.addEventListener(
      "pointerdown",
      handleOutsideClick
    );
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener(
        "pointerdown",
        handleOutsideClick
      );
      document.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!isEditing) {
      return;
    }

    requestAnimationFrame(() => {
      titleInputRef.current?.focus();
    });
  }, [isEditing]);

  const stopPropagation = (event) => {
    event.stopPropagation();
  };

  const finishEditing = (event) => {
    event.preventDefault();
    event.stopPropagation();

    onChange(id, {
      isNew: false,
      isEditing: false,
    });

    setMenuOpen(false);
  };

  const editHeader = (event) => {
    event.preventDefault();
    event.stopPropagation();

    onChange(id, {
      isNew: false,
      isEditing: true,
    });

    setMenuOpen(false);
  };

  const deleteHeader = (event) => {
    event.preventDefault();
    event.stopPropagation();

    setMenuOpen(false);
    onDelete(id);
  };

const toggleMenu = (event) => {
  event.preventDefault();
  event.stopPropagation();

  if (isDragging) {
    return;
  }

  if (
    !menuOpen &&
    menuButtonRef.current
  ) {
    const buttonRect =
      menuButtonRef.current
        .getBoundingClientRect();

    const scrollArea =
      menuButtonRef.current.closest(
        [
          "#scrollableTable",
          "#scrollableNetsTable",
          "#scrollablePilesTable",
        ].join(",")
      );

    const scrollAreaRect =
      scrollArea?.getBoundingClientRect();

    const visibleTop = Math.max(
      0,
      scrollAreaRect?.top ?? 0
    );

    const visibleBottom = Math.min(
      window.innerHeight,
      scrollAreaRect?.bottom ??
        window.innerHeight
    );

    const availableSpaceBelow =
      visibleBottom - buttonRect.bottom;

    const availableSpaceAbove =
      buttonRect.top - visibleTop;

    const menuRequiredHeight = 95;

    if (
      availableSpaceBelow >=
      menuRequiredHeight
    ) {
      setMenuPlacement("down");
    } else if (
      availableSpaceAbove >=
      menuRequiredHeight
    ) {
      setMenuPlacement("up");
    } else {
      setMenuPlacement(
        availableSpaceAbove >
          availableSpaceBelow
          ? "up"
          : "down"
      );
    }
  }

  setMenuOpen(
    (current) => !current
  );
};

  const handleInputKeyDown = (event) => {
    event.stopPropagation();

    if (event.key === "Enter") {
      finishEditing(event);
    }
  };

  return (
    <tr
      ref={setNodeRef}
      style={rowStyle}
      className={[
        "order-header-row",
        rightToLeft
          ? "order-header-row--rtl"
          : "order-header-row--ltr",
        isEditing ? "order-header-row--editing" : "",
        menuOpen ? "order-header-row--menu-open" : "",
        isDragging ? "order-header-row--dragging" : "",
      ].join(" ")}
    >
      <td
        colSpan={totalColumns}
        className="order-header-cell"
      >
        <div className="order-header-content">
          {/*
           * במצב עריכה מוצגים רק השדות וה־וי.
           * אין פח ואין כפתור פתיחה/סגירה.
           */}
          {isEditing ? (
            <>
              <div className="order-header-field order-header-title">
                <label htmlFor={`${id}-title`}>
                  כותרת
                </label>

                <input
                  ref={titleInputRef}
                  id={`${id}-title`}
                  type="text"
                  value={header.title || ""}
                  placeholder="בעזרת הכותרת ניתן להפריד אלמנטים"
                  onPointerDown={stopPropagation}
                  onClick={stopPropagation}
                  onKeyDown={handleInputKeyDown}
                  onChange={(event) =>
                    onChange(id, {
                      title: event.target.value,
                    })
                  }
                />
              </div>

              <div className="order-header-field order-header-note">
                <label htmlFor={`${id}-supplier-note`}>
                  הערות לספק
                </label>

                <input
                  id={`${id}-supplier-note`}
                  type="text"
                  value={header.supplierNote || ""}
                  placeholder="הערות לגבי אריזה, סימונים, ריתוכים וכדומה..."
                  onPointerDown={stopPropagation}
                  onClick={stopPropagation}
                  onKeyDown={handleInputKeyDown}
                  onChange={(event) =>
                    onChange(id, {
                      supplierNote: event.target.value,
                    })
                  }
                />
              </div>

              <button
                type="button"
                className="order-header-confirm"
                onPointerDown={stopPropagation}
                onClick={finishEditing}
                aria-label="שמירת ונעילת הכותרת"
                title="שמירת ונעילת הכותרת"
              >
                <FontAwesomeIcon icon="check" />
              </button>
            </>
          ) : (
            <>
              {/*
               * שש הנקודות קיימות רק בכותרת נעולה.
               * לחיצה פותחת עריכה/מחיקה.
               * גרירה מזיזה את הכותרת.
               */}
              <div
                ref={menuRef}
                className={[
                  "order-header-actions",
                  menuOpen
                    ? "order-header-actions--open"
                    : "",
                ].join(" ")}
              >
                <button
                  ref={handleMenuButtonRef}
                  type="button"
                  className="order-header-menu-button"
                  {...attributes}
                  {...listeners}
                  onClick={toggleMenu}
                  aria-label="אפשרויות וגרירת כותרת"
                  aria-expanded={menuOpen}
                  title="אפשרויות וגרירה"
                >
                  <span className="order-header-menu-dots">
                    <i />
                    <i />
                    <i />
                    <i />
                    <i />
                    <i />
                  </span>
                </button>

                {menuOpen && (
                  <div
                    className={[
                      "order-header-menu",
                      `order-header-menu--${menuPlacement}`,
                    ].join(" ")}
                    onPointerDown={stopPropagation}
                    onClick={stopPropagation}
                  >
                    <button
                      type="button"
                      className="order-header-menu-item"
                      onPointerDown={stopPropagation}
                      onClick={editHeader}
                    >
                      <span className="order-header-menu-item-icon">
                        ✎
                      </span>

                      <span>עריכה</span>
                    </button>

                    <button
                      type="button"
                      className="order-header-menu-item order-header-menu-item--delete"
                      onPointerDown={stopPropagation}
                      onClick={deleteHeader}
                    >
                      <FontAwesomeIcon icon="trash" />
                      <span>מחיקה</span>
                    </button>
                  </div>
                )}
              </div>

              <div className="order-header-display">
                <div className="order-header-display-field">
                  <span className="order-header-display-label">
                    כותרת:
                  </span>

                  <span className="order-header-display-title">
                    {header.title || "ללא כותרת"}
                  </span>
                </div>

                <div className="order-header-display-field order-header-display-note">
                  <span className="order-header-display-label">
                    הערות לספק:
                  </span>

                  <span className="order-header-display-note-text">
                    {header.supplierNote || "ללא הערות"}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

export default DraggableHeaderRow;